import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { ContentsClient, ContentVersionDto, SetupRequirement } from '../api/api-client';
import { CryptoHelper } from '../utils/cryptoHelper';
import {DecryptedContent} from "./useContents";
import type {WebSearchSource} from '../services/aiRequestTypes';

interface DecryptedContentVersionDto extends ContentVersionDto {
  decryptedData?: string;
  decryptedAttributes?: {
    webSearchSources?: WebSearchSource[];
    [key: string]: any; // Allow for future attributes
  };
}

interface DecryptedThread extends DecryptedContent {
  decryptedTitle?: string;
  decryptedMessages?: DecryptedContentVersionDto[];
  currentDraftId?: number | null;
  currentDraftContent?: string;
}

/**
 * Hook to fetch and decrypt a specific thread by ID
 */
export const useThread = (id: number | undefined, options?: { enabled?: boolean }) => {
  const { isAuthenticated, masterKey, setupNeeded } = useAuth();

  return useQuery({
    queryKey: ['thread', id],
    queryFn: async (): Promise<DecryptedThread> => {
      if (!id) throw new Error('Thread ID is required');
      
      const client = new ContentsClient();
      const threadData = await client.getContent(id);

      // If no master key, return thread without decrypted data
      if (!masterKey) {
        return {
          ...threadData,
          decryptedTitle: 'Encrypted Thread',
          decryptedMessages: [],
          contentKey: undefined,
          currentDraftId: null
        };
      }
      
      console.log(`Decrypting thread ${id} with master key`, masterKey);

      let decryptedTitle = 'Encrypted Thread';
      let decryptedMessages: DecryptedContentVersionDto[] = [];
      let contentKey: CryptoKey | undefined;
      let currentDraftId: number | null = null;
      let currentDraftContent: string | undefined;

      try {
        if (threadData.encryptedContentKey) {
          // Decrypt the content key
          contentKey = await CryptoHelper.decryptContentKey(threadData.encryptedContentKey, masterKey);

          // Decrypt title
          if (threadData.title && contentKey) {
            decryptedTitle = await CryptoHelper.decryptContent(threadData.title, contentKey);
          }

          // Decrypt messages
          if (threadData.messages && contentKey) {
            decryptedMessages = [];
            
            for (const message of threadData.messages) {
              if (message.encryptedData) {
                try {
                  const decryptedContent = await CryptoHelper.decryptContent(message.encryptedData, contentKey);
                  
                  // Check if this is a draft (user message that hasn't been submitted)
                  if (message.messageType === 'user' && !message.submittedAt) {
                    currentDraftId = message.id!;
                    currentDraftContent = decryptedContent;
                    // Don't add drafts to the messages array - they're for input restoration only
                    continue;
                  }
                  
                  // Decrypt attributes if present
                  let decryptedAttributes: { webSearchSources?: WebSearchSource[]; [key: string]: any } | undefined;
                  if (message.encryptedAttributes) {
                    try {
                      const decryptedAttrsString = await CryptoHelper.decryptContent(message.encryptedAttributes, contentKey);
                      decryptedAttributes = JSON.parse(decryptedAttrsString);
                      if (decryptedAttributes?.webSearchSources) {
                        console.log(`Decrypted web search sources for message ${message.id}:`, decryptedAttributes.webSearchSources);
                      }
                    } catch (error) {
                      console.warn(`Failed to decrypt attributes for message ${message.id}:`, error);
                    }
                  }

                  // Only add submitted messages to the UI
                  if (message.submittedAt) {
                    decryptedMessages.push({
                      ...message,
                      decryptedData: decryptedContent,
                      decryptedAttributes: decryptedAttributes
                    });
                  }
                } catch (error) {
                  console.warn(`Failed to decrypt message ${message.id}:`, error);
                }
              }
            }
          }
        }
      } catch (error) {
        console.warn(`Failed to decrypt thread ${id}:`, error);
      }

      return {
        ...threadData,
        decryptedTitle,
        decryptedMessages,
        contentKey,
        currentDraftId,
        currentDraftContent
      };
    },
    enabled: isAuthenticated && setupNeeded === SetupRequirement.None && !!masterKey && !!id && (options?.enabled !== false),
  });
};

export type { DecryptedThread, DecryptedContentVersionDto };